<template>
  <div class="opening-hours">
    <div v-for="(o, index) in openingHours" :key="index" class="opening-hour">
      <div class="row">
        <div class="col-xs-11">
          <div :class="{ 'has-error': shouldShowDayError(o) }">
            <el-select v-model="o.dayOfWeek" multiple @change="validateAndChange"
              placeholder="Seleccionar días de la semana">
              <el-option v-for="item in daysOfWeek" :key="item.value" :label="item.label" :value="item.value"
                :disabled="isOptionDisabled(item, o)">
              </el-option>
            </el-select>
          </div>
          <div :class="{ 'has-error': shouldShowTimeError(o) }">
            <el-time-select v-model="o.opens" :picker-options="{
              start: '06:00',
              step: '00:15',
              end: '21:00',
            }" placeholder="Seleccionar horario apertura" :editable="false" @change="() => validateTime(index)"
              required>
            </el-time-select>
          </div>
          <div :class="{ 'has-error': shouldShowTimeError(o) }">
            <el-time-select v-model="o.closes" :picker-options="{
              start: '06:00',
              step: '00:15',
              end: '21:00'
            }" placeholder="Seleccionar horario cierre" :editable="false" @change="() => validateTime(index)" required>
            </el-time-select>
          </div>
        </div>
        <div class="col-xs-1">
          <button type="button" class="btn btn-default" @click="deleteOpeningHour(index)">
            X
          </button>
        </div>
      </div>
    </div>

    <button type="button" class="btn btn-primary" @click="addOpeningHour" :disabled="!canAddMore || hasErrors">
      Agregar nueva secuencia horaria
    </button>
  </div>
</template>

<script>
import _ from "lodash";

export default {
  props: {
    value: { required: true },
  },

  data() {
    return {
      openingHours: _.isArray(this.value) && this.value.length > 0
        ? _.cloneDeep(this.value)
        : [{
          dayOfWeek: [],
          opens: '',
          closes: ''
        }],
      daysOfWeek: [
        { label: "Lunes", value: "monday" },
        { label: "Martes", value: "tuesday" },
        { label: "Miércoles", value: "wednesday" },
        { label: "Jueves", value: "thursday" },
        { label: "Viernes", value: "friday" },
        { label: "Sábado", value: "saturday" },
        { label: "Domingo", value: "sunday" },
      ],
      shouldValidate: false,
      validationTimer: null
    };
  },

  computed: {
    availableDays() {
      const selectedDays = _.flatten(
        this.openingHours.map((o) => o.dayOfWeek)
      );
      return this.daysOfWeek.filter((day) => !selectedDays.includes(day.value));
    },
    hasErrors() {
      return this.openingHours.some((o) =>
        !o.dayOfWeek.length ||
        !o.opens ||
        !o.closes ||
        this.hasTimeError(o)
      );
    },
    canAddMore() {
      return (
        this.openingHours.length === 0 ||
        (this.openingHours.every((o) => this.isOpeningHourValid(o)) &&
          this.availableDays.length > 0)
      );
    }
  },

  methods: {
    shouldShowDayError(openingHour) {
      // Solo mostrar error si el usuario ha interactuado con el campo
      return this.shouldValidate && !openingHour.dayOfWeek.length;
    },

    shouldShowTimeError(openingHour) {
      // Solo mostrar error si el usuario ha interactuado con el campo y hay un error real
      return this.shouldValidate &&
        ((openingHour.opens && openingHour.closes && this.hasTimeError(openingHour)) ||
          (openingHour.opens && !openingHour.closes) ||
          (!openingHour.opens && openingHour.closes));
    },

    validateTime(index) {
      const debouncedValidation = _.debounce(() => {
        const schedule = this.openingHours[index];
        if (schedule.opens && schedule.closes) {
          const [openHour, openMinute] = schedule.opens.split(':').map(Number);
          const [closeHour, closeMinute] = schedule.closes.split(':').map(Number);

          const openingMinutes = openHour * 60 + openMinute;
          const closingMinutes = closeHour * 60 + closeMinute;

          // Verifica la condición de error
          if (openingMinutes >= closingMinutes) {
            this.shouldValidate = true;
          }
        }
        this.validateAndChange();
      }, 300);

      debouncedValidation();
    },

    validateAndChange: _.debounce(function () {
      if (!this.shouldValidate) {
        this.shouldValidate = true;
        return;
      }

      const errors = {
        emptyDays: false,
        emptyTimes: false,
        invalidTimes: false
      };

      // Siempre emitir el valor actual
      this.$emit("input", _.cloneDeep(this.openingHours));

      if (this.openingHours.length === 0) {
        this.$emit("setHasEmptySchedule", true);
        this.$emit("scheduleErrors", { emptyDays: true });
        return;
      }

      // Validar cada horario
      for (const schedule of this.openingHours) {
        if (!schedule.dayOfWeek?.length) {
          errors.emptyDays = true;
          break;
        }
        if (!schedule.opens || !schedule.closes) {
          errors.emptyTimes = true;
          break;
        }
        if (this.hasTimeError(schedule)) {
          errors.invalidTimes = true;
          break;
        }
      }

      const hasErrors = errors.emptyDays || errors.emptyTimes || errors.invalidTimes;

      this.$emit("setHasEmptySchedule", errors.emptyDays);
      this.$emit("scheduleErrors", hasErrors ? errors : null);
    }, 300),

    isOpeningHourValid(openingHour) {
      return (
        openingHour.dayOfWeek.length > 0 &&
        openingHour.opens &&
        openingHour.closes &&
        !this.hasTimeError(openingHour)
      );
    },

    hasTimeError(openingHour) {
      if (!openingHour.opens || !openingHour.closes) return false;

      const [openHour, openMinute] = openingHour.opens.split(':').map(Number);
      const [closeHour, closeMinute] = openingHour.closes.split(':').map(Number);

      const openingMinutes = openHour * 60 + openMinute;
      const closingMinutes = closeHour * 60 + closeMinute;

      return openingMinutes >= closingMinutes;
    },

    isOptionDisabled(item, currentOpeningHour) {
      if (currentOpeningHour.dayOfWeek.includes(item.value)) return false;
      return this.openingHours.some(o =>
        o !== currentOpeningHour && o.dayOfWeek.includes(item.value)
      );
    },

    addOpeningHour() {
      if (this.canAddMore && !this.hasErrors) {
        this.openingHours.push({
          dayOfWeek: [],
          opens: "",
          closes: "",
        });
      }
    },

    deleteOpeningHour(index) {
      this.openingHours.splice(index, 1);
      this.validateAndChange();
    }
  },

  created() {
    this.$nextTick(() => {
      this.validateAndChange();
    });
  },

  watch: {
    value: {
      handler(newValue) {
        if (_.isArray(newValue) && newValue.length > 0) {
          this.openingHours = _.cloneDeep(newValue);
        }
      },
      immediate: true
    }
  },

  beforeDestroy() {
    clearTimeout(this.validationTimer);
  }
};
</script>

<style lang="scss" scoped>
.opening-hours {
  .opening-hour {
    margin-bottom: 20px;

    .el-select {
      display: block;
      margin-bottom: 10px;
    }

    .el-time-select {
      margin-bottom: 10px;
    }
  }

  .has-error {

    .el-select,
    .el-time-select {

      input,
      .el-input__inner {
        border-color: #a94442;
      }
    }
  }

  label.control-label {
    color: inherit !important;
  }
}
</style>